'use client';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  MessageSquare, Send, Plus, ChevronRight, Clock, CheckCheck,
  Package, Megaphone, CreditCard, HelpCircle, X, Loader2,
  AlertCircle, RefreshCw, Search,
} from 'lucide-react';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/components/PublicFooter';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


// ── Types ─────────────────────────────────────────────────────────────────────

interface SupportMessage {
  id: string;
  userId: string;
  orderId: string | null;
  subject: string;
  category: string;
  message: string;
  senderRole: 'customer' | 'admin';
  isRead: boolean;
  parentId: string | null;
  createdAt: string;
}

interface Thread {
  root: SupportMessage;
  replies: SupportMessage[];
}

const CATEGORIES = [
  { id: 'order', label: 'Order Issue', icon: Package, color: 'text-blue-400' },
  { id: 'campaign', label: 'Campaign / Delivery', icon: Megaphone, color: 'text-purple-400' },
  { id: 'billing', label: 'Billing & Payment', icon: CreditCard, color: 'text-amber-400' },
  { id: 'general', label: 'General Question', icon: HelpCircle, color: 'text-brand-400' },
];

function formatTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function categoryConfig(cat: string) {
  return CATEGORIES.find((c) => c.id === cat) ?? CATEGORIES[3];
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function SupportPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeThread, setActiveThread] = useState<Thread | null>(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // New thread form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newOrderId, setNewOrderId] = useState('');
  const [newMessage, setNewMessage] = useState('');

  // Reply
  const [replyText, setReplyText] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && !user) {
      router.replace('/sign-up-login');
    }
  }, [authLoading, user, router]);

  // ── Load threads ────────────────────────────────────────────────────────────
  const loadThreads = useCallback(async () => {
    if (!user) return;
    setLoadingThreads(true);
    setError('');
    try {
      // Fetch all root messages belonging to this customer
      const { data: rootData, error: rootErr } = await supabase
        .from('support_messages')
        .select('*')
        .eq('user_id', user.id)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (rootErr) throw rootErr;

      const roots: SupportMessage[] = (rootData ?? []).map((r: any) => ({
        id: r.id,
        userId: r.user_id,
        orderId: r.order_id,
        subject: r.subject,
        category: r.category,
        message: r.message,
        senderRole: r.sender_role,
        isRead: r.is_read,
        parentId: r.parent_id,
        createdAt: r.created_at,
      }));

      if (roots.length === 0) {
        setThreads([]);
        setLoadingThreads(false);
        return;
      }

      const rootIds = roots.map((r) => r.id);

      // Fetch all replies for these root messages (includes admin/manager replies)
      const { data: replyData, error: replyErr } = await supabase
        .from('support_messages')
        .select('*')
        .in('parent_id', rootIds)
        .order('created_at', { ascending: true });

      if (replyErr) throw replyErr;

      const mapMsg = (r: any): SupportMessage => ({
        id: r.id,
        userId: r.user_id,
        orderId: r.order_id,
        subject: r.subject,
        category: r.category,
        message: r.message,
        senderRole: r.sender_role,
        isRead: r.is_read,
        parentId: r.parent_id,
        createdAt: r.created_at,
      });

      const built: Thread[] = roots.map((root) => ({
        root,
        replies: (replyData ?? [])
          .filter((r: any) => r.parent_id === root.id)
          .map(mapMsg),
      }));

      setThreads(built);

      // Keep active thread in sync
      if (activeThread) {
        const updated = built.find((t) => t.root.id === activeThread.root.id);
        if (updated) setActiveThread(updated);
      }
    } catch (err: any) {
      console.error('[support] loadThreads error:', err);
      const msg = err?.message || err?.error_description || err?.details || 'Please try again.';
      setError(`Failed to load messages. ${msg}`);
    } finally {
      setLoadingThreads(false);
    }
  }, [user, supabase, activeThread?.root.id]);

  useEffect(() => {
    if (user) loadThreads();
  }, [user]);

  // ── Real-time subscription ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('support_messages_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'support_messages' },
        () => { loadThreads(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, supabase, loadThreads]);

  // ── Scroll to bottom on new messages ───────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeThread?.replies.length]);

  // ── Send new thread ─────────────────────────────────────────────────────────
  const handleNewThread = async () => {
    if (!user || !newSubject.trim() || !newMessage.trim()) return;
    setSendingMessage(true);
    setError('');
    try {
      const { error: insertError } = await supabase.from('support_messages').insert({
        user_id: user.id,
        order_id: newOrderId.trim() || null,
        subject: newSubject.trim(),
        category: newCategory,
        message: newMessage.trim(),
        sender_role: 'customer',
        is_read: false,
        parent_id: null,
        root_user_id: user.id,
      });
      if (insertError) throw insertError;
      setShowNewForm(false);
      setNewSubject('');
      setNewCategory('general');
      setNewOrderId('');
      setNewMessage('');
      await loadThreads();
    } catch (err: any) {
      setError('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // ── Send reply ──────────────────────────────────────────────────────────────
  const handleReply = async () => {
    if (!user || !activeThread || !replyText.trim()) return;
    setSendingMessage(true);
    setError('');
    try {
      const { error: insertError } = await supabase.from('support_messages').insert({
        user_id: user.id,
        order_id: activeThread.root.orderId,
        subject: activeThread.root.subject,
        category: activeThread.root.category,
        message: replyText.trim(),
        sender_role: 'customer',
        is_read: false,
        parent_id: activeThread.root.id,
        root_user_id: user.id,
      });
      if (insertError) throw insertError;
      setReplyText('');
      await loadThreads();
    } catch (err: any) {
      setError('Failed to send reply. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-950">
        <Loader2 className="animate-spin text-brand-400" size={32} />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-navy-950 flex flex-col">
      <PublicTopbar />
      <main className="flex-1 pt-20 pb-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="chip mb-2">Support</span>
              <h1 className="font-display text-3xl sm:text-4xl font-800 text-white">
                Message <span className="text-gradient">Support</span>
              </h1>
              <p className="text-slate-400 mt-1 text-sm">
                Ask questions about your orders, placements, and campaigns.
              </p>
            </div>
            <button
              onClick={() => { setShowNewForm(true); setActiveThread(null); }}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold btn-primary"
            >
              <Plus size={15} />
              New Message
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-3 p-4 mb-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <AlertCircle size={16} className="shrink-0" />
              {error}
              <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ minHeight: '600px' }}>

            {/* ── Thread List — hidden on mobile when a thread or form is active ── */}
            <div className={`lg:col-span-1 glass-card rounded-2xl overflow-hidden flex flex-col ${(activeThread || showNewForm) ? 'hidden lg:flex' : 'flex'}`}>
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Conversations</span>
                <button
                  onClick={loadThreads}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw size={14} />
                </button>
              </div>

              {/* Search */}
              <div className="px-4 py-3 border-b border-white/5">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search messages…"
                    className="w-full pl-8 pr-8 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-brand-500/50 transition-colors"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loadingThreads ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="animate-spin text-brand-400" size={24} />
                  </div>
                ) : threads.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <MessageSquare size={32} className="text-slate-600 mb-3" />
                    <p className="text-slate-400 text-sm">No messages yet.</p>
                    <p className="text-slate-500 text-xs mt-1">Start a conversation with our team.</p>
                  </div>
                ) : (() => {
                    const q = searchQuery.trim().toLowerCase();
                    const filtered = q
                      ? threads.filter((thread) => {
                          if (thread.root.subject.toLowerCase().includes(q)) return true;
                          if (thread.root.message.toLowerCase().includes(q)) return true;
                          if (thread.replies.some((r) => r.message.toLowerCase().includes(q))) return true;
                          return false;
                        })
                      : threads;

                    if (filtered.length === 0) {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                          <Search size={28} className="text-slate-600 mb-3" />
                          <p className="text-slate-400 text-sm">No results for "{searchQuery}"</p>
                          <p className="text-slate-500 text-xs mt-1">Try a different keyword.</p>
                        </div>
                      );
                    }

                    return filtered.map((thread) => {
                      const cat = categoryConfig(thread.root.category);
                      const CatIcon = cat.icon;
                      const isActive = activeThread?.root.id === thread.root.id;
                      const lastMsg = thread.replies.length > 0
                        ? thread.replies[thread.replies.length - 1]
                        : thread.root;
                      const hasAdminReply = thread.replies.some((r) => r.senderRole === 'admin');

                      return (
                        <button
                          key={thread.root.id}
                          onClick={() => { setActiveThread(thread); setShowNewForm(false); }}
                          className={`w-full text-left p-4 border-b border-white/5 transition-colors hover:bg-white/5 ${
                            isActive ? 'bg-brand-600/10 border-l-2 border-l-brand-500' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0 mt-0.5`}>
                              <CatIcon size={14} className={cat.color} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-0.5">
                                <p className="text-sm font-medium text-white truncate">{thread.root.subject}</p>
                                <span className="text-xs text-slate-500 shrink-0">{formatTime(lastMsg.createdAt)}</span>
                              </div>
                              <p className="text-xs text-slate-400 truncate">{lastMsg.message}</p>
                              <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-xs text-slate-500">{cat.label}</span>
                                {hasAdminReply && (
                                  <span className="flex items-center gap-1 text-xs text-green-400">
                                    <CheckCheck size={10} />
                                    Replied
                                  </span>
                                )}
                                {thread.replies.length > 0 && (
                                  <span className="text-xs text-slate-600">
                                    {thread.replies.length} {thread.replies.length === 1 ? 'reply' : 'replies'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    });
                  })()
                }
              </div>
            </div>

            {/* ── Right Panel: New Form or Thread View — hidden on mobile when neither is active ── */}
            <div className={`lg:col-span-2 glass-card rounded-2xl overflow-hidden flex flex-col ${(activeThread || showNewForm) ? 'flex' : 'hidden lg:flex'}`}>

              {/* New Message Form */}
              {showNewForm && (
                <div className="flex flex-col h-full">
                  <div className="p-5 border-b border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowNewForm(false)}
                        className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white transition-colors"
                      >
                        ← Back
                      </button>
                      <h2 className="text-base font-semibold text-white">New Message</h2>
                    </div>
                    <button
                      onClick={() => setShowNewForm(false)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* Category */}
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-2">Topic</label>
                      <div className="grid grid-cols-2 gap-2">
                        {CATEGORIES.map((cat) => {
                          const Icon = cat.icon;
                          return (
                            <button
                              key={cat.id}
                              onClick={() => setNewCategory(cat.id)}
                              className={`flex items-center gap-2.5 p-3 rounded-xl border text-sm font-medium transition-all ${
                                newCategory === cat.id
                                  ? 'border-brand-500/50 bg-brand-600/10 text-white' :'border-white/10 bg-white/3 text-slate-400 hover:border-white/20 hover:text-white'
                              }`}
                            >
                              <Icon size={15} className={cat.color} />
                              {cat.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Subject</label>
                      <input
                        type="text"
                        value={newSubject}
                        onChange={(e) => setNewSubject(e.target.value)}
                        placeholder="Brief description of your question"
                        className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-brand-500/50 transition-colors"
                      />
                    </div>

                    {/* Order ID (optional) */}
                    {(newCategory === 'order' || newCategory === 'campaign' || newCategory === 'billing') && (
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1.5">
                          Order Number <span className="text-slate-600">(optional)</span>
                        </label>
                        <input
                          type="text"
                          value={newOrderId}
                          onChange={(e) => setNewOrderId(e.target.value)}
                          placeholder="e.g. ZAB-2026-1234"
                          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-brand-500/50 transition-colors"
                        />
                      </div>
                    )}

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1.5">Message</label>
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Describe your question or issue in detail..."
                        rows={5}
                        className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-brand-500/50 transition-colors resize-none"
                      />
                    </div>
                  </div>

                  <div className="p-5 border-t border-white/5">
                    <button
                      onClick={handleNewThread}
                      disabled={sendingMessage || !newSubject.trim() || !newMessage.trim()}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingMessage ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      Send Message
                    </button>
                  </div>
                </div>
              )}

              {/* Thread View */}
              {!showNewForm && activeThread && (
                <div className="flex flex-col h-full">
                  {/* Thread Header */}
                  <div className="p-5 border-b border-white/5">
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => setActiveThread(null)}
                        className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-300 hover:text-white transition-colors shrink-0 self-center"
                      >
                        ← Back
                      </button>
                      {(() => {
                        const cat = categoryConfig(activeThread.root.category);
                        const CatIcon = cat.icon;
                        return (
                          <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                            <CatIcon size={16} className={cat.color} />
                          </div>
                        );
                      })()}
                      <div className="min-w-0">
                        <h2 className="text-base font-semibold text-white truncate">{activeThread.root.subject}</h2>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          <span className="text-xs text-slate-500">{categoryConfig(activeThread.root.category).label}</span>
                          {activeThread.root.orderId && (
                            <>
                              <ChevronRight size={10} className="text-slate-600" />
                              <span className="text-xs text-slate-500 font-mono">{activeThread.root.orderId}</span>
                            </>
                          )}
                          <span className="text-xs text-slate-600">{formatTime(activeThread.root.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Root message */}
                    <MessageBubble msg={activeThread.root} isOwn={true} />

                    {/* Replies — filter out internal notes for customers */}
                    {activeThread.replies
                      .filter((r: any) => !r.isInternal && !r.is_internal)
                      .map((reply) => (
                        <MessageBubble
                          key={reply.id}
                          msg={reply}
                          isOwn={reply.senderRole === 'customer'}
                        />
                      ))}

                    {activeThread.replies.filter((r: any) => !r.isInternal && !r.is_internal).length === 0 && (
                      <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                        <Clock size={12} />
                        Our team typically responds within 1–2 business days.
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>

                  {/* Reply Box */}
                  <div className="p-4 border-t border-white/5">
                    <div className="flex gap-3">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleReply();
                        }}
                        placeholder="Type a follow-up message… (Ctrl+Enter to send)"
                        rows={2}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-brand-500/50 transition-colors resize-none"
                      />
                      <button
                        onClick={handleReply}
                        disabled={sendingMessage || !replyText.trim()}
                        className="self-end flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingMessage ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!showNewForm && !activeThread && (
                <div className="flex-1 flex flex-col items-center justify-center py-20 px-6 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-brand-600/10 border border-brand-500/20 flex items-center justify-center mb-4">
                    <MessageSquare size={28} className="text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Select a conversation</h3>
                  <p className="text-slate-400 text-sm max-w-xs">
                    Choose a thread from the left, or start a new message to get help with your orders, placements, or campaigns.
                  </p>
                  <button
                    onClick={() => setShowNewForm(true)}
                    className="mt-6 flex items-center gap-2 px-5 py-2.5 text-sm font-semibold btn-primary"
                  >
                    <Plus size={14} />
                    New Message
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

// ── Message Bubble ────────────────────────────────────────────────────────────

function MessageBubble({ msg, isOwn }: { msg: SupportMessage; isOwn: boolean }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
          isOwn
            ? 'bg-brand-600/20 border border-brand-500/20 text-white rounded-br-sm' :'bg-white/5 border border-white/10 text-slate-200 rounded-bl-sm'
        }`}
      >
        {!isOwn && (
          <p className="text-xs font-semibold text-brand-400 mb-1">Support Team</p>
        )}
        <p className="leading-relaxed whitespace-pre-wrap">{msg.message}</p>
        <p className={`text-xs mt-1.5 ${isOwn ? 'text-brand-400/60' : 'text-slate-500'}`}>
          {new Date(msg.createdAt).toLocaleString('en-US', {
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  );
}
